import * as React from 'react';
import type { FileAttachmentItem } from '../components/file-attachments';

const DEFAULT_MAX_ATTACHMENTS = 10;
const DEFAULT_MAX_FILE_SIZE = 25_000_000;
const DEFAULT_MAX_TOTAL_SIZE = 30_000_000;

interface PendingAttachment<TRecord> extends FileAttachmentItem {
  record?: TRecord;
}

export interface UseAttachmentUploadMessages {
  maxAttachments: (max: number) => string;
  fileTooLarge: string;
  attachmentsTooLarge: string;
  uploadFailed: string;
}

const DEFAULT_MESSAGES: UseAttachmentUploadMessages = {
  maxAttachments: (max) => `Max ${max} attachments`,
  fileTooLarge: 'File is too large',
  attachmentsTooLarge: 'Attachments too large',
  uploadFailed: 'Upload failed',
};

export interface UseAttachmentUploadOptions<TRecord> {
  upload: (file: File) => Promise<TRecord>;
  enabled: boolean;
  maxAttachments?: number;
  maxFileSize?: number;
  maxTotalSize?: number;
  messages?: Partial<UseAttachmentUploadMessages>;
  // Scopes an actual drop's upload to this element — window-level drag detection
  // means every enabled hook instance sees every drop, so when more than one
  // instance can be enabled at once (e.g. an inline and a floating composer for
  // different accounts), an unscoped drop would upload into both.
  dropZoneRef?: React.RefObject<HTMLElement | null>;
}

export function useAttachmentUpload<TRecord>({
  upload,
  enabled,
  maxAttachments = DEFAULT_MAX_ATTACHMENTS,
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
  maxTotalSize = DEFAULT_MAX_TOTAL_SIZE,
  messages,
  dropZoneRef,
}: UseAttachmentUploadOptions<TRecord>) {
  const msg = React.useMemo(() => ({ ...DEFAULT_MESSAGES, ...messages }), [messages]);
  const [items, setItems] = React.useState<PendingAttachment<TRecord>[]>([]);
  const itemsRef = React.useRef(items);
  itemsRef.current = items;

  const addFiles = React.useCallback(
    (files: File[]) => {
      if (!enabled) return;

      const prev = itemsRef.current;
      // Rejected files never uploaded — exclude them so a single bounced
      // attachment doesn't permanently eat into the count/size budget.
      const prevCounted = prev.filter((i) => i.status !== 'error');
      let runningCount = prevCounted.length;
      let runningSize = prevCounted.reduce((sum, i) => sum + (i.size ?? 0), 0);
      const next = [...prev];
      const toUpload: { id: string; file: File }[] = [];

      for (const file of files) {
        const id = crypto.randomUUID();

        if (runningCount >= maxAttachments) {
          next.push({ id, name: file.name, size: file.size, status: 'error', error: msg.maxAttachments(maxAttachments) });
          continue;
        }
        if (file.size > maxFileSize) {
          next.push({ id, name: file.name, size: file.size, status: 'error', error: msg.fileTooLarge });
          continue;
        }
        if (runningSize + file.size > maxTotalSize) {
          next.push({ id, name: file.name, size: file.size, status: 'error', error: msg.attachmentsTooLarge });
          continue;
        }

        runningCount += 1;
        runningSize += file.size;
        next.push({ id, name: file.name, size: file.size, type: file.type, status: 'uploading' });
        toUpload.push({ id, file });
      }

      itemsRef.current = next;
      setItems(next);

      for (const { id, file } of toUpload) {
        upload(file)
          .then((record) => {
            setItems((current) => current.map((i) => (i.id === id ? { ...i, status: 'ready', record } : i)));
          })
          .catch((err: unknown) => {
            const message = err instanceof Error ? err.message : msg.uploadFailed;
            setItems((current) => current.map((i) => (i.id === id ? { ...i, status: 'error', error: message } : i)));
          });
      }
    },
    [enabled, upload, maxAttachments, maxFileSize, maxTotalSize, msg]
  );

  const remove = React.useCallback((id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const clear = React.useCallback(() => setItems([]), []);

  const uploading = items.some((i) => i.status === 'uploading');

  const toAttachments = React.useCallback(
    (): TRecord[] => items.filter((i) => i.status === 'ready' && i.record).map((i) => i.record as TRecord),
    [items]
  );

  const [isDraggingOver, setIsDraggingOver] = React.useState(false);
  // The window fires dragenter/dragleave as the pointer crosses child elements
  // too, so a plain boolean flickers off mid-drag; a depth counter only clears
  // at zero. Kept in a ref (not a variable local to the effect below) so it
  // survives the effect re-subscribing when addFiles's identity changes
  // mid-drag — otherwise a fresh closure's counter would restart at 0 while
  // isDraggingOver (real component state) stays wherever it was, desyncing
  // the two and letting a stray dragleave clear the flag early.
  const dragDepthRef = React.useRef(0);

  // Window-level, not element-level: a file dragged in from Finder/Explorer
  // should surface the drop target the moment it enters the browser window,
  // not only once the pointer happens to cross the small input box.
  React.useEffect(() => {
    if (!enabled) return;

    const isFileDrag = (event: DragEvent) => Array.from(event.dataTransfer?.types ?? []).includes('Files');

    const onDragEnter = (event: DragEvent) => {
      if (!isFileDrag(event)) return;
      event.preventDefault();
      dragDepthRef.current += 1;
      setIsDraggingOver(true);
    };
    const onDragOver = (event: DragEvent) => {
      if (!isFileDrag(event)) return;
      event.preventDefault();
    };
    const onDragLeave = (event: DragEvent) => {
      if (!isFileDrag(event)) return;
      dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
      if (dragDepthRef.current === 0) setIsDraggingOver(false);
    };
    const onDrop = (event: DragEvent) => {
      if (!isFileDrag(event)) return;
      event.preventDefault();
      dragDepthRef.current = 0;
      setIsDraggingOver(false);
      if (dropZoneRef && !dropZoneRef.current?.contains(event.target as Node)) return;
      addFiles(Array.from(event.dataTransfer?.files ?? []));
    };

    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('drop', onDrop);
    };
  }, [enabled, addFiles, dropZoneRef]);

  const handlePaste = React.useCallback(
    (event: React.ClipboardEvent) => {
      if (!enabled) return;
      const files = Array.from(event.clipboardData.items)
        .filter((item) => item.kind === 'file')
        .map((item) => item.getAsFile())
        .filter((file): file is File => file !== null);
      if (files.length > 0) addFiles(files);
    },
    [enabled, addFiles]
  );

  return { items, addFiles, remove, clear, uploading, toAttachments, isDraggingOver, handlePaste };
}
